import type { Middleware } from "@reduxjs/toolkit";
import { sparxsEditorBlueprintFetchedHydrated } from "@/features/sparxsEditor/store/domainActions";
import { setSparxFromSession } from "@/features/sparxsEditor/store/slices/document/documentSlice";
import { withInteractionBlueprintFileName } from "@/features/sparxsEditor/lib/blueprint/withInteractionBlueprintFileName";
import {
  isSessionEntryDirty,
  type SessionSparxEntry,
} from "@/features/sparxsEditor/store/slices/session/sessionSlice";
import { rootReducer } from "@/store/app/rootReducer";

/**
 * Prevents automatic blueprint hydrate from overwriting dirty editor session (Save-enabled).
 * When `sparxsEditorBlueprintFetchedHydrated` would replace session for a file the user is
 * editing, forwards `setSparxFromSession` from existing session instead so document state stays
 * aligned without applying server blueprint until conflict resolution or save.
 */
export const sparxsEditorBlueprintHydrateGuardMiddleware: Middleware<
  object,
  ReturnType<typeof rootReducer>
> = (store) => (next) => (action) => {
  if (!sparxsEditorBlueprintFetchedHydrated.match(action)) {
    return next(action);
  }
  const { fileName } = action.payload;
  const entry: SessionSparxEntry | undefined =
    store.getState().editor.sparxSession.byFileName[fileName];
  if (entry != null && isSessionEntryDirty(entry)) {
    return next(
      setSparxFromSession({
        data: withInteractionBlueprintFileName(entry.current, fileName),
        originalData: withInteractionBlueprintFileName(
          entry.original,
          fileName,
        ),
      }),
    );
  }
  return next(action);
};
